import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import { WithLoader } from 'Components/WithLoader'
import {
  useGetAllowedTokensParameterGroupQuery,
  useGetCustomerGroupParameterGroupQuery,
  useGetProductGroupParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import { useSingleProduct } from 'Services/ProductManagement/Features/Product/Hooks'
import { ProductTabType } from 'Services/ProductManagement/Features/Product/productSlice'
import TariffList from 'Services/ProductManagement/Features/Tariff/Components/TariffList'
import { useTariffsWithParams } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { useGetActiveVatDefinitionsQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import {
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import { useConfirm } from 'Util/hooks/useConfirm'
import useHash from 'Util/hooks/useHash'
import SnackbarUtils from 'Util/Snackbar'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Grid, Paper, Tab } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

type TabId = ProductTabType.DETAILS | ProductTabType.TARIFFS

export default function ProductDetailPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/products/`
  const previousLocation = usePreviousLocation(baseUrl)
  const navigate = useNavigate()
  const { id } = useParams()
  const hash = useHash()
  const { confirm } = useConfirm()
  const { tariffs, loading, total_results, params, setParams } = useTariffsWithParams()
  const { singleProduct, singleLoading, deleteProduct, deleteLoading } = useSingleProduct(id)
  const {
    data: productGroupParameterGroup = null,
    refetch: fetchProductGroups,
    isLoading: isProductGroupParameterGroupLoading
  } = useGetProductGroupParameterGroupQuery(undefined)
  const {
    data: customerGroupParameterGroup = null,
    refetch: fetchCustomerGroups,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery(undefined)
  const {
    data: allowedTokensParameterGroup = null,
    refetch: fetchAllowedTokens,
    isLoading: isAllowedTokensParameterGroupLoading
  } = useGetAllowedTokensParameterGroupQuery(undefined)
  const { data: activeVatDefinitions = [], isFetching: isActiveVatDefinitionsFetching } =
    useGetActiveVatDefinitionsQuery(undefined)

  // Active vat definitions endpoint returns vat definitions as a list,
  // but the list contains only one vat definition
  // since there can be only one active at a time
  const activeVatDefinition = activeVatDefinitions.length > 0 ? activeVatDefinitions[0] : null

  const defaultTab = (hash as TabId) || ProductTabType.DETAILS
  const [tabValue, setTabValue] = useState(defaultTab)

  useEffect(() => {
    fetchProductGroups()
    fetchCustomerGroups()
    fetchAllowedTokens()
  }, [])

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Products`,
      link: baseUrl
    },
    {
      title: singleProduct ? getTranslation(singleProduct.translations, 'fi').name : ''
    }
  ]

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabId) => {
    navigate(`#${newValue}`)
    setTabValue(newValue)
  }

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const handleDelete = () => {
    if (!id) return
    deleteProduct(id)
      .then(unwrapResult)
      .then(() => {
        navigate(baseUrl)
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while deleting the product.`)
      })
  }

  const isLoading =
    loading === 'pending' || singleLoading === 'pending' || deleteLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Breadcrumbs items={breadcrumbs} />
          <Box display="flex" justifyContent="space-between">
            <BackButton label={t`Back`} link={previousLocation} />
            <Box>
              <DeleteButton
                sx={{ mr: 1 }}
                onClick={() =>
                  confirm({
                    title: t`Are you sure?`,
                    message: t`Are you sure you want to delete the product?`,
                    handleConfirm: () => {
                      handleDelete()
                    }
                  })
                }
              >{t`Delete product`}</DeleteButton>
              <CustomButton
                onClick={() => navigate(`${baseUrl}/${id}/edit`)}
              >{t`Edit product`}</CustomButton>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4}>
        <Grid size={8}>
          <Box sx={{ my: 2 }}>
            <TabContext value={tabValue}>
              <Paper>
                <TabList onChange={handleTabChange} aria-label={t`Content tab list`}>
                  <Tab label={t`Product details`} value={ProductTabType.DETAILS} />
                  <Tab label={t`Tariffs`} value={ProductTabType.TARIFFS} />
                </TabList>
              </Paper>
              <Box>
                <TabPanel sx={{ px: 0, pt: 2 }} value={ProductTabType.DETAILS}>
                  <WithLoader loading={isLoading}>
                    {singleProduct && (
                      <ProductDetails
                        product={singleProduct}
                        productGroupParameterGroup={productGroupParameterGroup}
                        isProductGroupParameterGroupLoading={isProductGroupParameterGroupLoading}
                        customerGroupParameterGroup={customerGroupParameterGroup}
                        isCustomerGroupParameterGroupLoading={isCustomerGroupParameterGroupLoading}
                        allowedTokensParameterGroup={allowedTokensParameterGroup}
                        isAllowedTokensParameterGroupLoading={isAllowedTokensParameterGroupLoading}
                        vatDefinition={activeVatDefinition}
                        isVatDefinitionsLoading={isActiveVatDefinitionsFetching}
                      />
                    )}
                  </WithLoader>
                </TabPanel>
              </Box>
              <TabPanel sx={{ px: 0, pt: 2 }} value={ProductTabType.TARIFFS}>
                <WithLoader rows={ROWS_PER_PAGE_DEFAULT} loading={isLoading} height={33}>
                  {singleProduct && (
                    <TariffList
                      rows={tariffs}
                      isLoading={isLoading}
                      params={params}
                      handleRequestSort={handleRequestSort}
                      handleChangePage={handleChangePage}
                      count={total_results}
                      page={
                        params.start_index
                          ? parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT
                          : 0
                      }
                    />
                  )}
                </WithLoader>
              </TabPanel>
            </TabContext>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
